/**
 * 🛒 AdminCustomersPage - Lista de Todos os Clientes
 * 
 * Página administrativa para listar todos os clientes que compraram produtos
 * na plataforma, de todos os produtores
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config/api';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import StatCard from '../../components/common/StatCard';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import { Drawer } from '../../theme/components';
import './AdminCustomersPage.scss';

const AdminCustomersPage = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({
    total_customers: 0,
    total_orders: 0,
    total_spent: 0,
    avg_spent_per_customer: 0,
    avg_ticket_value: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('total_spent');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [producers, setProducers] = useState([]);
  const [selectedProducer, setSelectedProducer] = useState('');

  /**
   * 🔍 Verificar se usuário é admin
   */
  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Acesso negado. Apenas administradores podem acessar esta página.');
      setLoading(false);
      return;
    }
  }, [user]);

  /**
   * 📊 Carregar dados dos clientes
   */
  useEffect(() => {
    if (user?.role === 'admin') {
      loadCustomers();
      loadStats();
      loadProducers();
    }
  }, [user, sortBy, sortOrder, pagination.currentPage, selectedProducer]);

  /**
   * 🚀 Carregar clientes do backend
   */
  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError('');

      const offset = (pagination.currentPage - 1) * pagination.itemsPerPage;
      const limit = pagination.itemsPerPage;
      
      let url = `${API_URL}/api/admin/customers?sort=${sortBy}&order=${sortOrder}&limit=${limit}&offset=${offset}`;
      
      if (selectedProducer) {
        url += `&producer=${encodeURIComponent(selectedProducer)}`;
      }

      const token = localStorage.getItem('auth_token');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setCustomers(data.customers || []);
      
      // Atualizar paginação
      if (data.pagination) {
        setPagination(prev => ({
          ...prev,
          totalPages: data.pagination.pages,
          totalItems: data.pagination.total
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
      setError('Erro ao carregar dados dos clientes. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 📊 Carregar estatísticas do backend
   */
  const loadStats = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/customers/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setStats(data.stats || {
        total_customers: 0,
        total_orders: 0,
        total_spent: 0,
        avg_spent_per_customer: 0,
        avg_ticket_value: 0
      });
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
      // Não mostrar erro para stats, apenas usar valores padrão
    }
  };

  /**
   * 🔍 Filtrar clientes por termo de busca
   */
  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower) ||
      customer.document?.includes(searchTerm)
    );
  });

  /**
   * 📊 Converter valores para números (backend retorna strings)
   */
  const formatStats = {
    total: parseInt(stats.total_customers) || 0,
    totalSpent: parseInt(stats.total_spent) || 0,
    totalOrders: parseInt(stats.total_orders) || 0,
    avgOrderValue: parseFloat(stats.avg_ticket_value) || 0
  };

  /**
   * 🔍 Abrir drawer do cliente
   */
  const openCustomerDrawer = async (customer) => {
    setSelectedCustomer(customer);
    setHistoryLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/customers/${customer.email}/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setCustomerHistory(data.history || []);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
      setCustomerHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  /**
   * ❌ Fechar drawer do cliente
   */
  const closeCustomerDrawer = () => {
    setSelectedCustomer(null);
    setCustomerHistory([]);
    setSelectedSale(null);
  };

  /**
   * 📋 Abrir modal de detalhes da venda
   */
  const openSaleModal = (sale) => {
    setSelectedSale(sale);
  };

  /**
   * ❌ Fechar modal de venda
   */
  const closeSaleModal = () => {
    setSelectedSale(null);
  };

  /**
   * 🏭 Carregar lista de produtores
   */
  const loadProducers = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/customers/producers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setProducers(data.producers || []);
    } catch (err) {
      console.error('Erro ao carregar produtores:', err);
      setProducers([]);
    }
  };

  /**
   * 📄 Mudar página
   */
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  /**
   * 🔍 Limpar filtros
   */
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProducer('');
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  if (user?.role !== 'admin') {
    return (
      <div className="admin-customers-page">
        <div className="access-denied">
          <span className="access-denied__icon">🚫</span>
          <span className="access-denied__title">Acesso Negado</span>
          <span className="access-denied__message">
            Apenas administradores podem acessar esta página.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-customers-page">
      {/* Header */}
      <AdminPageHeader
        icon="solar:users-group-rounded-bold"
        title="Clientes da Plataforma"
        subtitle="Todos os clientes que compraram produtos na plataforma"
      />

      {/* Estatísticas */}
      <div className="row g-3" style={{ marginBottom: '24px' }}>
        <div className="col-xxl-3 col-sm-6">
          <StatCard
            variant="blue"
            title="Total de Clientes"
            value={formatStats.total}
            change="Clientes únicos na plataforma"
            icon="solar:users-group-rounded-bold"
          />
        </div>
        <div className="col-xxl-3 col-sm-6">
          <StatCard
            variant="green"
            title="Valor Total Gasto"
            value={CurrencyFormatter.centsToFormatted(formatStats.totalSpent)}
            change="Receita total dos clientes"
            icon="solar:dollar-bold"
          />
        </div>
        <div className="col-xxl-3 col-sm-6">
          <StatCard
            variant="purple"
            title="Total de Pedidos"
            value={formatStats.totalOrders}
            change="Pedidos realizados"
            icon="solar:cart-large-bold"
          />
        </div>
        <div className="col-xxl-3 col-sm-6">
          <StatCard
            variant="orange"
            title="Ticket Médio"
            value={CurrencyFormatter.centsToFormatted(formatStats.avgOrderValue)}
            change="Valor médio por pedido"
            icon="solar:chart-bold"
          />
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="card shadow-sm" style={{ marginBottom: '24px' }}>
        <div className="card-body">
          <div className="row g-3">
            {/* Busca */}
            <div className="col-xxl-4 col-md-6">
              <label className="form-label fw-semibold">Buscar Cliente</label>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <i className="ri-search-line"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Nome, email ou documento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Filtro por Produtor */}
            <div className="col-xxl-3 col-md-6">
              <label className="form-label fw-semibold">Produtor</label>
              <select
                value={selectedProducer}
                onChange={(e) => setSelectedProducer(e.target.value)}
                className="form-select"
              >
                <option value="">Todos os Produtores</option>
                {producers.map((producer) => (
                  <option key={producer.email} value={producer.email}>
                    {producer.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Ordenar por */}
            <div className="col-xxl-3 col-md-6">
              <label className="form-label fw-semibold">Ordenar por</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="form-select"
              >
                <option value="total_spent">Valor Gasto</option>
                <option value="total_orders">Número de Pedidos</option>
                <option value="name">Nome</option>
                <option value="last_purchase">Última Compra</option>
              </select>
            </div>

            {/* Ordem */}
            <div className="col-xxl-2 col-md-6">
              <label className="form-label fw-semibold">Ordem</label>
              <div className="d-flex gap-2">
                <button
                  onClick={() => setSortOrder('desc')}
                  className={`btn ${sortOrder === 'desc' ? 'btn-primary' : 'btn-outline-secondary'} flex-fill`}
                >
                  <i className="ri-arrow-down-line"></i> Desc
                </button>
                <button
                  onClick={() => setSortOrder('asc')}
                  className={`btn ${sortOrder === 'asc' ? 'btn-primary' : 'btn-outline-secondary'} flex-fill`}
                >
                  <i className="ri-arrow-up-line"></i> Asc
                </button>
              </div>
            </div>
          </div>

          {/* Botão de Limpar Filtros */}
          {(searchTerm || selectedProducer) && (
            <div className="mt-3">
              <button
                onClick={clearFilters}
                className="btn btn-outline-danger btn-sm"
              >
                <i className="ri-delete-bin-line me-1"></i>
                Limpar Filtros
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lista de Clientes */}
      <div className="admin-customers-page__content">
        {loading ? (
          <div className="loading-state">
            <span className="loading-state__spinner">⏳</span>
            <span className="loading-state__text">Carregando clientes...</span>
          </div>
        ) : error ? (
          <div className="error-state">
            <span className="error-state__icon">❌</span>
            <span className="error-state__text">{error}</span>
            <button onClick={loadCustomers} className="error-state__button">
              Tentar Novamente
            </button>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state__icon">📭</span>
            <span className="empty-state__text">
              {searchTerm ? 'Nenhum cliente encontrado para a busca.' : 'Nenhum cliente encontrado.'}
            </span>
          </div>
        ) : (
          <div className="customers-list">
            {filteredCustomers.map((customer) => (
              <div 
                key={customer.id} 
                className="customer-card"
                onClick={() => openCustomerDrawer(customer)}
                style={{ cursor: 'pointer' }}
              >
                <div className="customer-card__avatar">
                  <span className="customer-card__avatar-text">
                    {customer.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                
                <div className="customer-card__info">
                  <div className="customer-card__name">
                    {customer.name || 'Nome não informado'}
                  </div>
                  <div className="customer-card__email">
                    {customer.email}
                  </div>
                  <div className="customer-card__document">
                    {customer.document ? `CPF: ${customer.document}` : 'Documento não informado'}
                  </div>
                </div>

                <div className="customer-card__stats">
                  <div className="customer-card__stat">
                    <span className="customer-card__stat-value">
                      {CurrencyFormatter.centsToFormatted(customer.total_spent || 0)}
                    </span>
                    <span className="customer-card__stat-label">Total Gasto</span>
                  </div>
                  <div className="customer-card__stat">
                    <span className="customer-card__stat-value">
                      {customer.total_orders || 0}
                    </span>
                    <span className="customer-card__stat-label">Pedidos</span>
                  </div>
                  <div className="customer-card__stat">
                    <span className="customer-card__stat-value">
                      {customer.last_purchase ? 
                        new Date(customer.last_purchase).toLocaleDateString('pt-BR') : 
                        'Nunca'
                      }
                    </span>
                    <span className="customer-card__stat-label">Última Compra</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginação */}
        {!loading && !error && filteredCustomers.length > 0 && (
          <div className="pagination">
            <div className="pagination__info">
              Mostrando {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} a{' '}
              {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} de{' '}
              {pagination.totalItems} clientes
            </div>
            
            <div className="pagination__controls">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="pagination__button pagination__button--prev"
              >
                ← Anterior
              </button>
              
              <div className="pagination__pages">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, pagination.currentPage - 2) + i;
                  if (pageNum > pagination.totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`pagination__page ${
                        pageNum === pagination.currentPage ? 'pagination__page--active' : ''
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="pagination__button pagination__button--next"
              >
                Próximo →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer do Cliente */}
      <Drawer
        show={!!selectedCustomer}
        onHide={closeCustomerDrawer}
        title={`${selectedCustomer?.name || 'Cliente'}`}
        subtitle="Detalhes completos do cliente"
        icon="mdi:account"
        size="lg"
        footer={
          <Drawer.Button
            variant="secondary"
            onClick={closeCustomerDrawer}
          >
            Fechar
          </Drawer.Button>
        }
      >
        {selectedCustomer ? (
          <>
            {/* Informações Básicas */}
            <Drawer.Section title="Informações Básicas" icon="mdi:information">
              <Drawer.Row label="Nome" value={selectedCustomer.name || 'Nome não informado'} />
              <Drawer.Row label="Email" value={selectedCustomer.email} />
              <Drawer.Row 
                label="Documento" 
                value={selectedCustomer.document ? `CPF: ${selectedCustomer.document}` : 'Documento não informado'} 
              />
              {selectedCustomer.phone && (
                <Drawer.Row label="Telefone" value={selectedCustomer.phone} />
              )}
            </Drawer.Section>

            <Drawer.Divider />

            {/* Estatísticas */}
            <Drawer.Section title="Estatísticas" icon="mdi:chart-line">
              <Drawer.Row 
                label="Total Gasto" 
                value={CurrencyFormatter.centsToFormatted(selectedCustomer.total_spent || 0)}
                highlight
              />
              <Drawer.Row 
                label="Total de Pedidos" 
                value={selectedCustomer.total_orders || 0}
                highlight
              />
              <Drawer.Row 
                label="Última Compra" 
                value={selectedCustomer.last_purchase ? 
                  new Date(selectedCustomer.last_purchase).toLocaleDateString('pt-BR') : 
                  'Nunca'
                }
              />
            </Drawer.Section>

            <Drawer.Divider />

            {/* Histórico de Compras */}
            <Drawer.Section title="Histórico de Compras" icon="mdi:shopping">
              {historyLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="text-muted mt-2">Carregando histórico...</p>
                </div>
              ) : customerHistory.length === 0 ? (
                <Drawer.EmptyState
                  icon="mdi:shopping-outline"
                  text="Nenhuma compra encontrada"
                />
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {customerHistory.map((purchase) => (
                    <div 
                      key={purchase.id} 
                      style={{
                        padding: '12px',
                        marginBottom: '12px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => openSaleModal(purchase)}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#f1f5f9';
                        e.target.style.borderColor = '#cbd5e1';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8fafc';
                        e.target.style.borderColor = '#e2e8f0';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            {purchase.product_name || 'Produto não informado'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            Produtor: {purchase.producer_name || 'Não informado'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                            {new Date(purchase.created_at).toLocaleDateString('pt-BR')}
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>
                            {CurrencyFormatter.centsToFormatted(purchase.amount || 0)}
                          </div>
                        </div>
                        <div style={{ marginLeft: '12px' }}>
                          <span 
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor: purchase.status === 'paid' ? '#d1fae5' : '#fef3c7',
                              color: purchase.status === 'paid' ? '#065f46' : '#92400e'
                            }}
                          >
                            {purchase.status === 'paid' ? 'Pago' : purchase.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Drawer.Section>
          </>
        ) : (
          <Drawer.EmptyState
            icon="mdi:alert"
            text="Não foi possível carregar os detalhes do cliente."
          />
        )}
      </Drawer>

      {/* Modal de Detalhes da Venda */}
      {selectedSale && (
        <div className="sale-modal-overlay" onClick={closeSaleModal}>
          <div className="sale-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sale-modal__header">
              <div className="sale-modal__title">
                <span className="sale-modal__icon">📋</span>
                <span className="sale-modal__text">Detalhes da Venda</span>
              </div>
              <button className="sale-modal__close" onClick={closeSaleModal}>
                ✕
              </button>
            </div>

            <div className="sale-modal__content">
              <div className="sale-details">
                <div className="sale-detail">
                  <span className="sale-detail__label">ID da Venda:</span>
                  <span className="sale-detail__value">#{selectedSale.id}</span>
                </div>
                <div className="sale-detail">
                  <span className="sale-detail__label">Produto:</span>
                  <span className="sale-detail__value">{selectedSale.product_name || 'Não informado'}</span>
                </div>
                <div className="sale-detail">
                  <span className="sale-detail__label">Produtor:</span>
                  <span className="sale-detail__value">{selectedSale.producer_name || 'Não informado'}</span>
                </div>
                <div className="sale-detail">
                  <span className="sale-detail__label">Email do Produtor:</span>
                  <span className="sale-detail__value">{selectedSale.producer_email || 'Não informado'}</span>
                </div>
                <div className="sale-detail">
                  <span className="sale-detail__label">Valor:</span>
                  <span className="sale-detail__value">
                    {CurrencyFormatter.centsToFormatted(selectedSale.amount || 0)}
                  </span>
                </div>
                <div className="sale-detail">
                  <span className="sale-detail__label">Status:</span>
                  <span className={`status-badge status-badge--${selectedSale.status}`}>
                    {selectedSale.status === 'paid' ? 'Pago' : selectedSale.status}
                  </span>
                </div>
                <div className="sale-detail">
                  <span className="sale-detail__label">Data da Compra:</span>
                  <span className="sale-detail__value">
                    {new Date(selectedSale.created_at).toLocaleString('pt-BR')}
                  </span>
                </div>
                <div className="sale-detail">
                  <span className="sale-detail__label">Última Atualização:</span>
                  <span className="sale-detail__value">
                    {new Date(selectedSale.updated_at).toLocaleString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomersPage;
